import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';

export interface TemperatureChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  comparisonData?: ChartDataPoint[];
  comparisonLabel?: string;
  dateTimeConfig?: DateTimeConfig;
  /** When provided, uses category x-axis instead of datetime (for analysis charts) */
  categories?: string[];
}

export const temperatureChart = (
  options: TemperatureChartOptions,
): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    height = 250,
    comparisonData,
    comparisonLabel,
    dateTimeConfig,
    categories,
  } = options;

  const hasRange = ['D', 'W', 'M'].includes(granularity);

  const series: SeriesConfig[] = [
    {
      id: 'temperature',
      type: 'line',
      dataKey: 'T',
      name: 'Temperature',
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      showMarkers: false,
      zIndex: 2,
    },
  ];

  if (hasRange) {
    series.push({
      id: 'temperature-range',
      type: 'areaRange',
      dataKey: 'T_RANGE',
      rangeKey: 'T_RANGE',
      name: 'Min / Max',
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      fillOpacity: 0.2,
      lineWidth: 0,
      showMarkers: false,
      zIndex: 0,
    });
  }

  if (comparisonData) {
    series.push({
      id: 'temperature-comparison',
      type: 'line',
      dataKey: 'T_COMP',
      name: comparisonLabel ?? 'Comparison',
      color: theme.colors.textSecondary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      dashArray: '5 3',
      showMarkers: false,
      zIndex: 1,
    });
  }

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({
      type: categories ? 'category' : 'datetime',
      timezone,
      granularity,
      theme,
      categories,
    }),
    yAxes: [
      createYAxisConfig({
        id: 'temperature',
        metric: 'T',
        units,
        theme,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'T',
      units,
      theme,
      dateTimeConfig,
      tooltipLabels: categories,
    }),
    legend: createLegendConfig(series.length > 2, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
